import * as React from 'react'

import { cn } from '../lib/cn'

export interface CircularProgressProps {
  value: number
  renderLabel?: (progress: number) => number | string
  size?: number
  strokeWidth?: number
  circleStrokeWidth?: number
  progressStrokeWidth?: number
  shape?: 'square' | 'round'
  className?: string
  progressClassName?: string
  labelClassName?: string
  showLabel?: boolean
}

export const CircularProgress = React.forwardRef<HTMLDivElement, CircularProgressProps>(
  (
    {
      value,
      renderLabel,
      className,
      progressClassName,
      labelClassName,
      showLabel,
      shape = 'round',
      size = 50,
      strokeWidth,
      circleStrokeWidth = 6,
      progressStrokeWidth = 6,
    },
    ref,
  ) => {
    const clamped = Math.min(100, Math.max(0, value))
    const radius = size / 2 - 3
    const circumference = Math.ceil(3.14 * radius * 2)
    const percentage = Math.ceil(circumference * ((100 - clamped) / 100))
    const viewBox = `0 0 ${size} ${size}`

    return (
      <div ref={ref} className='relative' style={{ maxHeight: size }}>
        <svg
          width={size}
          height={size}
          viewBox={viewBox}
          version='1.1'
          xmlns='http://www.w3.org/2000/svg'
          style={{ transform: 'rotate(-90deg)' }}
          className='relative'
        >
          <circle
            r={radius}
            cx={size / 2}
            cy={size / 2}
            fill='transparent'
            strokeWidth={strokeWidth ?? circleStrokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset='0'
            className={cn('stroke-primary/25', className)}
          />
          <circle
            r={radius}
            cx={size / 2}
            cy={size / 2}
            strokeWidth={strokeWidth ?? progressStrokeWidth}
            strokeLinecap={shape}
            strokeDashoffset={percentage}
            fill='transparent'
            strokeDasharray={circumference}
            className={cn('stroke-primary', progressClassName)}
          />
        </svg>
        {showLabel && (
          <div className={cn('absolute inset-0 flex items-center justify-center', labelClassName)}>
            {renderLabel ? renderLabel(clamped) : clamped}
          </div>
        )}
      </div>
    )
  },
)
CircularProgress.displayName = 'CircularProgress'
